'use client';

import { useTranslations } from 'next-intl';
import { Card } from '../../components/card';
import { Icon } from '../../components/icon';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Link02Icon } from '@terra/ui-icons';
import type { LinkedFindingRowData } from './LinkedFindingRow';
import { LinkedFindingRow } from './LinkedFindingRow';
import type { InternalEnrichedFinding } from '../../types';
import { isArchivedEntity } from '@terra/shared-dto-types';
import { FindingChainFlow } from './finding-chain-graph';
import { BodyStrong } from '../../components/typography';

type LinkedFindingsSectionProps = {
  findings: InternalEnrichedFinding[];
  onNavigateToFinding?: (findingId: string) => void;
  variant?: 'card' | 'plain';
  chainOrder?: string[];
};
const toRowData = (finding: InternalEnrichedFinding): LinkedFindingRowData => ({
  id: finding.id,
  title: finding.title ?? null,
  status: finding.status ?? null,
  severity: finding.severity ?? null,
  createdAt: finding.createdAt,
  discoveryDate: finding.discoveryDate ?? null,
  isArchived: isArchivedEntity(finding),
});

export const LinkedFindingsSection = ({
  findings,
  onNavigateToFinding,
  variant = 'card',
  chainOrder,
}: LinkedFindingsSectionProps) => {
  const t = useTranslations('designSystem.signals.drawer');

  if (findings.length === 0) {
    return null;
  }

  const isChainView = !!chainOrder && chainOrder.length > 0;
  const body = isChainView ? (
    <FindingChainFlow
      findings={findings.map((finding) => ({
        ...toRowData(finding),
        discoveryDate: finding.discoveryDate ?? finding.createdAt ?? null,
      }))}
      orderedIds={chainOrder}
      onFindingClick={onNavigateToFinding}
    />
  ) : (
    <VStack gap={2} alignItems="stretch" w="full">
      {findings.map((finding) => (
        <LinkedFindingRow
          key={finding.id}
          finding={toRowData(finding)}
          onNavigate={onNavigateToFinding}
        />
      ))}
    </VStack>
  );

  if (variant === 'plain') {
    return body;
  }

  return (
    <Card.Root w="full" borderRadius="lg" boxShadow="sm">
      <Card.Header>
        <HStack gap={2} alignItems="center">
          <Icon size="sm">
            <Link02Icon />
          </Icon>
          <BodyStrong>
            {t('findingsCount', { count: findings.length })}
          </BodyStrong>
        </HStack>
      </Card.Header>
      <Card.Body>{body}</Card.Body>
    </Card.Root>
  );
};
